import {
  DispatchRequestDataTable,
  PAGINATION_PAGE_SIZES,
  ResizeableContent,
  ThemedPage,
  useApp,
  usePagination
} from "@ots-frontend/ots-ui";
import {Card, Container, Group, Menu, rem, Stack, Text, Title, UnstyledButton} from "@mantine/core";
import {useCallback, useMemo} from "react";
import {dispatchRequestsSelector, useGetUnauthorizedDispatchRequestsQuery} from "@ots-frontend/ots-data";
import {IconChevronDown} from "@tabler/icons-react";
import {useTranslation} from "react-i18next";
import {useNavigate} from "react-router-dom";
import {skipToken} from "@reduxjs/toolkit/query";

export type UnauthorizedDispatchRequestPageProps = {}

export const UnauthorizedDispatchRequestPage = (props: UnauthorizedDispatchRequestPageProps) => {

  const navigate = useNavigate();
  const {regionId} = useApp();
  const {t} = useTranslation(["translation", "common"]);

  const selectDispatchRequests = useMemo(() => {
    return dispatchRequestsSelector();
  }, []);

  const {
    dispatchRequests,
    isFetching,
    refetch
  } = useGetUnauthorizedDispatchRequestsQuery(regionId ?? skipToken, {
    pollingInterval: 120000,
    refetchOnMountOrArgChange: 120,
    selectFromResult: (result) => ({
      ...result,
      dispatchRequests: selectDispatchRequests(result, {regionId}),
      isFetching: result.isFetching,
    })
  });

  const pagination = usePagination({
    data: dispatchRequests,
    recordsPerPageOptions: PAGINATION_PAGE_SIZES,
    defaultPageSize: PAGINATION_PAGE_SIZES[1]
  });

  const onNewClick = useCallback(() => {
    navigate("form");
  }, [navigate]);

  return (
    <ThemedPage>
      {(classes) => (
        <ResizeableContent>
          {(size) => (
            <Container h={size.height} w={size.width} className={classes.container} fluid={true} p="md">
              <Stack h="100%" m="sm">
                <Card shadow="sm" padding="lg" mb="lg" radius="md" withBorder>
                  <Group position="apart">
                    <Title
                      order={4}>{t("page.unauthorizeddispatchrequests.title", "Unauthorized Dispatch Requests", {ns: "translation"})}</Title>
                    <Menu
                      width={260}
                      position="bottom-end"
                      transitionProps={{transition: "pop-top-right"}}
                      withinPortal
                    >
                      <Menu.Target>
                        <UnstyledButton>
                          <Group spacing={7}>
                            <Text weight={500} size="sm" sx={{lineHeight: 1}} mr={3}>
                              {t('common.action.plural', "Actions")}
                            </Text>
                            <IconChevronDown size={rem(12)} stroke={1.5}/>
                          </Group>
                        </UnstyledButton>
                      </Menu.Target>
                      <Menu.Dropdown>
                        <Menu.Item onClick={onNewClick}>
                          {t("actions.new", "New", {ns: "common"})}
                        </Menu.Item>
                        <Menu.Item onClick={() => refetch()}>
                          {t("actions.refresh", "Refresh", {ns: "common"})}
                        </Menu.Item>
                      </Menu.Dropdown>
                    </Menu>
                  </Group>
                </Card>
                <DispatchRequestDataTable
                  {...pagination}
                  withBorder
                  fetching={isFetching}/>
              </Stack>
            </Container>
          )}
        </ResizeableContent>
      )}
    </ThemedPage>
  );
};
